import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminContactMessagesService } from '../services/adminContactMessagesService';

const INQUIRY_LABELS = {
  general: 'General Inquiry',
  booking: 'Booking Support',
  technical: 'Technical Issue',
  vendor: 'Vendor Inquiry',
};

const STATUS_LABELS = {
  new: 'New',
  read: 'Read',
  replied: 'Replied',
};

export const useAdminContactMessages = (initialFilters = {}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    status: '',
    inquiry_type: '',
    search: '',
    page: 1,
    ...initialFilters,
  });

  const cleanFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
  }, [filters]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['adminContactMessages', cleanFilters],
    queryFn: () => adminContactMessagesService.getList(cleanFilters),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminContactMessagesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminContactMessages']);
      queryClient.invalidateQueries(['adminNotifications']);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => adminContactMessagesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminContactMessages']);
    },
  });

  const replyMutation = useMutation({
    mutationFn: ({ id, reply }) => adminContactMessagesService.reply(id, reply),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminContactMessages']);
      queryClient.invalidateQueries(['adminNotifications']);
    },
  });

  const messages = data?.data || [];
  const meta = data?.meta || {};
  const statistics = data?.statistics || { total: 0, new: 0, read: 0, replied: 0 };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, ...(key === 'status' || key === 'inquiry_type' ? { page: 1 } : {}) }));
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  return {
    messages,
    isLoading,
    error,
    filters,
    meta,
    statistics,
    handleFilterChange,
    handlePageChange,
    updateMessage: updateMutation.mutate,
    deleteMessage: deleteMutation.mutate,
    replyMessage: replyMutation.mutate,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isReplying: replyMutation.isPending,
    INQUIRY_LABELS,
    STATUS_LABELS,
  };
};
